import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { Box } from '@amzn/awsui-components-console';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import ReactFlow, {
	Background,
	Controls,
	useEdgesState,
	useNodesState,
	MiniMap,
	addEdge,
	ConnectionLineType,
	Node,
	ReactFlowInstance,
} from 'reactflow';
import { KeyAnyValuePairsObject } from 'src/@types/common';
import { useAuthContext } from 'src/providers/AuthContext';
import { isUndoShortcut, isRedoShortcut } from 'src/utils/CommonUtils';
import {
	defaultEdgeOptions,
	defaultEdgeStyles,
	generateGraphForDisplay,
	isStartOrEndNode,
	nodeTypes,
} from './updateWFDefinitionCanvasUtils';
import { ReactFlowNodeData } from './updateWFDefinitionCanvas.interface';
import { ReactFlowNodeStatus } from '../customNode/customNodeEnums';
import { createTaskDefinitionFromType } from '../createTaskDefinitionUtils';
import * as tokens from '@amzn/awsui-design-tokens';

interface UpdateWFDefinitionCanvasProps {
	onNodeClick?: () => void;
}

export default function UpdateWFDefinitionCanvas({
	onNodeClick: onNodeClickProp,
}: UpdateWFDefinitionCanvasProps = {}) {
	const [isDeletionAllowed, setIsDeletionAllowed] = useState(true);
	const { taskDefinitions, dependencies } = useStore(
		(state: Store) => state.WFDefinition
	);

	const { layoutedEdges, layoutedNodes } = React.useMemo(() => {
		return generateGraphForDisplay(taskDefinitions, dependencies, true);
	}, [taskDefinitions, dependencies]);
	const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

	const reactFlowWrapper = useRef<HTMLInputElement>(null);
	const [reactFlowInstance, setReactFlowInstance] = useState<
		ReactFlowInstance | KeyAnyValuePairsObject
	>({});
	const setActiveTaskDefinitionStore = useStore(
		(state: Store) => state.setActiveTaskDefinitionStore
	);
	const entityType = useStore(
		(state: Store) => state.workflowCreationMetaData.entityType
	);
	const resetActiveTaskDefinitionStore = useStore(
		(state: Store) => state.resetActiveTaskDefinitionStore
	);
	const updateReactFlowInstance = useStore(
		(state: Store) => state.updateReactFlowInstance
	);
	const fetchChildWorkflowDefinition = useStore(
		(state: Store) => state.fetchChildWorkflowDefinition
	);
	const updateReactFlowNodeDetails = useStore(
		(state: Store) => state.updateReactFlowNodeDetails
	);
	const saveToHistory = useStore((state: Store) => state.saveToHistory);
	const undo = useStore((state: Store) => state.undo);
	const redo = useStore((state: Store) => state.redo);
	const canUndo = useStore((state: Store) => state.canUndo);
	const canRedo = useStore((state: Store) => state.canRedo);
	const clearHistory = useStore((state: Store) => state.clearHistory);
	const updateCanDelete = useStore((state: Store) => state.updateCanDelete);
	const authContext = useAuthContext();
	const userName = authContext.userName;

	React.useEffect(() => {
		if (nodes.length > 0 && reactFlowInstance.getNodes) {
			clearHistory();
			saveToHistory();
		}
	}, [taskDefinitions, dependencies]);

	React.useEffect(() => {
		if (reactFlowInstance) {
			updateReactFlowInstance(reactFlowInstance);
		}
	}, [reactFlowInstance]);

	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (isUndoShortcut(event) && canUndo) {
				event.preventDefault();
				undo();
			}
			else if (isRedoShortcut(event) && canRedo) {
				event.preventDefault();
				redo();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [canUndo, canRedo, undo, redo]);

	const onConnect = useCallback(
		(params) => {
			setEdges((eds) =>
				addEdge(
					{
						...params,
						...defaultEdgeStyles,
					},
					eds
				)
			);
			setTimeout(() => saveToHistory(), 0);
		},
		[setEdges, saveToHistory]
	);

	const onNodeClick = (event, node) => {
		event.preventDefault();
		node.style = {
			color: 'red',
		};
		if (isStartOrEndNode(node)) {
			resetActiveTaskDefinitionStore();
			setIsDeletionAllowed(false);

			return;
		}

		setIsDeletionAllowed(true);
		setActiveTaskDefinitionStore(
			{ ...node.data.taskDefinition },
			node.id,
			node.data.nodeAdditionalProps.status !== ReactFlowNodeStatus.CREATING
		);

		if (onNodeClickProp) {
			onNodeClickProp();
		}
	};

	const onPaneClick = () => {
		resetActiveTaskDefinitionStore();
	};

	const onDrop = useCallback(
		(event) => {
			event.preventDefault();
			if (reactFlowWrapper.current === null) {
				return;
			}

			const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
			const node = JSON.parse(
				event.dataTransfer.getData('application/reactflow')
			);
			const { type, taskName } = node;

			if (typeof type === 'undefined' || !type) {
				return;
			}

			const position = reactFlowInstance.project({
				x: event.clientX - reactFlowBounds.left,
				y: event.clientY - reactFlowBounds.top,
			});
			const newNode: Node<ReactFlowNodeData> = {
				id: uuidv4(),
				type: `${type}`,
				position,
				data: {
					nodeAdditionalProps: {
						status: ReactFlowNodeStatus.CREATING,
						isClickable: true,
						isIncoming: true,
						name: taskName,
					},
					taskDefinition: createTaskDefinitionFromType(
						node,
						userName as string,
						entityType
					),
				},
			};

			setNodes((nds) => nds.concat(newNode));
			setTimeout(() => saveToHistory(), 0);

			if (node.isSubWorkflow && node.childWorkflowId) {
				fetchChildWorkflowDefinition(node.childWorkflowId).then(
					(childWorkflowDefinition) => {
						if (!childWorkflowDefinition && reactFlowInstance.getNode?.(newNode.id)) {
							updateReactFlowNodeDetails(newNode.id, {
								status: ReactFlowNodeStatus.CREATION_ERROR,
							});
						}
					}
				);
			}
		},
		[reactFlowInstance, setNodes, saveToHistory]
	);

	const onDragOver = useCallback((event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	}, []);

	const onNodesDelete = () => {
		resetActiveTaskDefinitionStore();
		setTimeout(() => saveToHistory(), 0);
	};

	const onEdgeClick = () => {
		if (!isDeletionAllowed) {
			setIsDeletionAllowed(true);
		}
	};

	const onNodeDragStop = useCallback(() => {
		saveToHistory();
	}, [saveToHistory]);

	const onEdgesDelete = useCallback(() => {
		setTimeout(() => saveToHistory(), 0);
	}, [saveToHistory]);

	const onSelectionChange = useCallback(() => {
		updateCanDelete();
	}, [updateCanDelete]);

	return (
		<Box className="h100 w100">
			<div style={{ height: '100%', width: '100%' }} ref={reactFlowWrapper}>
				<ReactFlow
					nodes={nodes}
					edges={edges}
					nodeTypes={nodeTypes}
					onNodesChange={onNodesChange}
					defaultEdgeOptions={defaultEdgeOptions}
					onNodesDelete={onNodesDelete}
					onEdgesChange={onEdgesChange}
					onEdgesDelete={onEdgesDelete}
					onConnect={onConnect}
					onDragOver={onDragOver}
					onInit={setReactFlowInstance}
					onDrop={onDrop}
					onEdgeClick={onEdgeClick}
					onNodeClick={onNodeClick}
					onPaneClick={onPaneClick}
					onNodeDragStop={onNodeDragStop}
					onSelectionChange={onSelectionChange}
					fitView
					minZoom={0.1}
					maxZoom={1.5}
					attributionPosition="bottom-left"
					connectionLineType={ConnectionLineType.SmoothStep}
					deleteKeyCode={isDeletionAllowed ? 'Backspace' : ''}
				>
					{/* Halved from React Flow defaults (200x150) to keep the minimap compact */}
					<MiniMap style={{ width: 100, height: 72 }} />
					<Controls />
					<Background color={tokens.colorBorderDividerDefault} gap={16} />
				</ReactFlow>
			</div>
		</Box>
	);
}
